import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { isActivityUnread } from "@/features/notifications/activity-mapping";
import { useActivityFeed } from "@/features/notifications/hooks/use-activity-feed";
import { useMarkNotificationsRead } from "@/features/notifications/hooks/use-mark-notifications-read";
import type { AppNotification } from "@/features/notifications/types";

interface NotificationsContextValue {
  error: Error | null;
  isLoading: boolean;
  isUnread: (notification: AppNotification) => boolean;
  markAllRead: () => void;
  /**
   * Dismisses a single notification's unread indicator for this browser
   * session only. There is no persisted per-row read column (the backend
   * tracks one read watermark per profile — see design.md decision 6), so
   * this does not survive a reload; `markAllRead` is the durable action.
   */
  markOneRead: (id: string) => void;
  notifications: AppNotification[];
  retry: () => void;
  unreadCount: number;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

/** Provides the live activity feed and read-state actions to shell and inbox surfaces. */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const feed = useActivityFeed();
  const { refetch } = feed;
  // Destructure the stable `mutate` fn — the useMutation return object itself
  // is recreated every render in TanStack Query v5, and depending on it would
  // rebuild markAllRead (and the context value) on every render.
  const { mutate: mutateMarkRead } = useMarkNotificationsRead();
  const [dismissedIds, setDismissedIds] = useState<ReadonlySet<string>>(() => new Set());
  /** Optimistic local watermark so the badge/list update before the feed refetches. */
  const [optimisticReadAt, setOptimisticReadAt] = useState<string | null>(null);

  const notifications = useMemo(() => feed.data?.notifications ?? [], [feed.data]);

  const effectiveLastReadAt = useMemo(() => {
    const serverWatermark = feed.data?.lastReadAt ?? null;
    if (!optimisticReadAt) {
      return serverWatermark;
    }
    if (!serverWatermark) {
      return optimisticReadAt;
    }
    return new Date(optimisticReadAt) > new Date(serverWatermark)
      ? optimisticReadAt
      : serverWatermark;
  }, [feed.data, optimisticReadAt]);

  const isUnread = useCallback(
    (notification: AppNotification) =>
      !dismissedIds.has(notification.id) &&
      isActivityUnread(notification.createdAt, effectiveLastReadAt),
    [dismissedIds, effectiveLastReadAt],
  );

  const unreadCount = useMemo(
    () => notifications.filter((item) => isUnread(item)).length,
    [notifications, isUnread],
  );

  const markAllRead = useCallback(() => {
    const previous = optimisticReadAt;
    const next = new Date().toISOString();
    setOptimisticReadAt(next);
    mutateMarkRead(undefined, {
      // Roll back to the pre-click watermark if the RPC fails, so the badge
      // and list don't get stuck showing 0 unread until a reload. Restoring
      // the captured value (not null) keeps an earlier watermark intact if
      // two clicks race, and the current-value guard avoids clobbering a
      // newer optimistic update from a later click.
      onError: () => {
        setOptimisticReadAt((current) => (current === next ? previous : current));
      },
    });
  }, [mutateMarkRead, optimisticReadAt]);

  const markOneRead = useCallback((id: string) => {
    setDismissedIds((previous) => new Set(previous).add(id));
  }, []);

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  const value = useMemo<NotificationsContextValue>(
    () => ({
      error: feed.error,
      isLoading: feed.isLoading,
      isUnread,
      markAllRead,
      markOneRead,
      notifications,
      retry,
      unreadCount,
    }),
    [
      feed.error,
      feed.isLoading,
      isUnread,
      markAllRead,
      markOneRead,
      notifications,
      retry,
      unreadCount,
    ],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

/** Returns notification state and read actions from the nearest `NotificationsProvider`. */
export function useNotifications(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error("useNotifications must be used within a NotificationsProvider.");
  }
  return context;
}
